import React from "react";

// Distance from an edge at which scrolling starts.
const SCROLL_THRESHOLD = 90;
// Pixels scrolled per frame when the pointer sits right on the edge.
const MAX_SCROLL_SPEED = 18;
// How far the pointer may stray past an edge and still keep scrolling.
const OUTSIDE_MARGIN = 80;

// Nearest vertical overflow owner starting at `start`. The drop area itself is
// normally the answer; walking up only matters if the ref ever points at a
// wrapper that is not the scrolling element. Whether it currently overflows is
// deliberately not part of the test, so a container that only scrolls at some
// breakpoints still wins over an unrelated scrollable ancestor.
const resolveScrollTarget = (start: HTMLElement | null): HTMLElement | null => {
  let node: HTMLElement | null = start;
  while (node) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (
      overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay"
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

/**
 * Auto-scrolls `containerRef` while an HTML5 drag is in progress and the
 * pointer is near its top or bottom edge.
 *
 * react-dnd's HTML5Backend drives drags with native drag events, and browsers
 * do not fire `mousemove` while a native drag is active — only `drag` and
 * `dragover`. Tracking the pointer with `mousemove` therefore never produces a
 * position, which is why the list stayed put when an item was dragged to the
 * edge. Pointer tracking here listens to the drag events instead.
 */
export const useDragAutoScroll = (
  containerRef?: React.RefObject<HTMLElement | null>,
) => {
  React.useEffect(() => {
    if (!containerRef) return;

    let pointerY: number | null = null;
    let scrollTarget: HTMLElement | null = null;
    let frame: number | null = null;

    const trackPointer = (event: DragEvent) => {
      // Firefox reports 0/0 on `drag` between real updates; ignore those.
      if (event.clientX === 0 && event.clientY === 0) return;
      pointerY = event.clientY;
    };

    const step = () => {
      frame = requestAnimationFrame(step);
      if (pointerY === null || !scrollTarget) return;

      const rect = scrollTarget.getBoundingClientRect();
      if (
        pointerY < rect.top - OUTSIDE_MARGIN ||
        pointerY > rect.bottom + OUTSIDE_MARGIN
      ) {
        return;
      }

      const maxScrollTop =
        scrollTarget.scrollHeight - scrollTarget.clientHeight;
      if (maxScrollTop <= 0) return;

      const distanceFromTop = pointerY - rect.top;
      const distanceFromBottom = rect.bottom - pointerY;

      // Clamped so a pointer dragged past the edge scrolls at full speed
      // instead of wrapping around to a negative ratio.
      const speedFor = (distance: number) =>
        Math.max(
          1,
          MAX_SCROLL_SPEED * Math.min(1, 1 - distance / SCROLL_THRESHOLD),
        );

      if (distanceFromTop < SCROLL_THRESHOLD && scrollTarget.scrollTop > 0) {
        scrollTarget.scrollTop = Math.max(
          0,
          scrollTarget.scrollTop - speedFor(distanceFromTop),
        );
      } else if (
        distanceFromBottom < SCROLL_THRESHOLD &&
        scrollTarget.scrollTop < maxScrollTop
      ) {
        scrollTarget.scrollTop = Math.min(
          maxScrollTop,
          scrollTarget.scrollTop + speedFor(distanceFromBottom),
        );
      }
    };

    const stop = () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
      pointerY = null;
      scrollTarget = null;
      document.removeEventListener("dragover", trackPointer, true);
      document.removeEventListener("drag", trackPointer, true);
    };

    // Any drag on the page arms the loop; the pointer-in-bounds check above
    // keeps it inert for drags that never reach this container.
    const start = () => {
      if (frame !== null) return;
      scrollTarget = resolveScrollTarget(containerRef.current);
      if (!scrollTarget) return;
      document.addEventListener("dragover", trackPointer, true);
      document.addEventListener("drag", trackPointer, true);
      frame = requestAnimationFrame(step);
    };

    document.addEventListener("dragstart", start, true);
    document.addEventListener("dragend", stop, true);
    document.addEventListener("drop", stop, true);

    return () => {
      stop();
      document.removeEventListener("dragstart", start, true);
      document.removeEventListener("dragend", stop, true);
      document.removeEventListener("drop", stop, true);
    };
  }, [containerRef]);
};

export default useDragAutoScroll;
